import {
  CloudTeamQuota,
  CmsTeamQuota,
  Quota,
  QuotaLimit,
  QuotaType,
  ServiceName,
  TeamQuota,
} from "../interfaces"
import { t } from "../lib/i18n"
import { ServiceNameMap } from "../lib/service"

import { isTrue, toNumberOrNull } from "./numberHelpers"

// プランがその項目を持たないと行ごと来ない。0 と読むと「上限 0」になってしまうので
// 未計測として扱う。
const unmeasuredQuota = (): Quota => ({
  includedQuota: null,
  purchasedQuota: 0,
  totalQuota: null,
  usedQuota: 0,
  unlimited: false,
  measured: false,
  status: "active",
})

// unlimited は「上限でエンフォースしない」側に倒れる一番緩いフラグなので、
// measured / total_quota と同じ硬さにする。"false" や 1 を無制限と読ませない。
const deserializeUnlimited = (value: unknown): boolean => isTrue(value)

// unlimited / measured を返さない旧 auth-api 互換。数値が来ていれば計測済みと読む。
const deserializeMeasured = (attributes: any, totalQuota: number | null) => {
  if (typeof attributes.measured === "boolean") return attributes.measured

  return totalQuota !== null
}

const quotaDeserializer = (obj: any, type: QuotaType): Quota => {
  const data = obj.find((item: any) => item.attributes.product_name === type)
  if (!data) return unmeasuredQuota()

  const totalQuota = toNumberOrNull(data.attributes.total_quota)

  return {
    includedQuota: toNumberOrNull(data.attributes.included_quota),
    purchasedQuota: data.attributes.purchased_quota ?? 0,
    totalQuota: totalQuota,
    usedQuota: data.attributes.used_quota ?? 0,
    unlimited: deserializeUnlimited(data.attributes.unlimited),
    measured: deserializeMeasured(data.attributes, totalQuota),
    status: data.attributes.status,
  }
}

const cmsTeamQuotaDeserializer = (data: any): CmsTeamQuota => {
  return {
    membership: quotaDeserializer(data, "membership"),
    bandwidth: quotaDeserializer(data, "bandwidth"),
    form: quotaDeserializer(data, "form"),
  }
}

const cloudTeamQuotaDeserializer = (data: any): CloudTeamQuota => {
  return {
    membership: quotaDeserializer(data, "membership"),
    buildTime: quotaDeserializer(data, "build_time"),
    customDomain: quotaDeserializer(data, "custom_domain"),
    bandwidth: quotaDeserializer(data, "bandwidth"),
    site: quotaDeserializer(data, "site"),
  }
}

export const teamQuotaDeserializer = (
  obj: any,
  serviceName: ServiceName,
): TeamQuota | null => {
  const data = obj.data
  if (!data.length) return null

  if (serviceName === ServiceNameMap.CMS) {
    return cmsTeamQuotaDeserializer(data)
  } else {
    return cloudTeamQuotaDeserializer(data)
  }
}

export const updatedQuotaDeserializer = (data: any): Quota => {
  const totalQuota = toNumberOrNull(data.total_quota)

  return {
    includedQuota: toNumberOrNull(data.included_quota),
    purchasedQuota: data.purchased_quota ?? 0,
    totalQuota: totalQuota,
    usedQuota: data.used_quota ?? 0,
    unlimited: deserializeUnlimited(data.unlimited),
    measured: deserializeMeasured(data, totalQuota),
    status: "active",
  }
}

// 上限の読み取り口。totalQuota を直接読まず必ずここを通す。
export const quotaLimit = (quota: Quota): QuotaLimit => {
  // unlimited を measured より先に見る。無制限プランで TeamQuota の行がまだ
  // 無いときは unlimited: true と measured: false の両方が立つ。
  if (quota.unlimited) return { kind: "unlimited" }
  if (!quota.measured || quota.totalQuota === null)
    return { kind: "unmeasured" }

  return { kind: "limited", value: quota.totalQuota }
}

// あといくつ使えるか。無制限・未計測では上限で止めてはいけないので、
// "limited" 以外はそのまま返して呼び出し側に判断させる。
export const remainingQuota = (quota: Quota): QuotaLimit => {
  const limit = quotaLimit(quota)
  if (limit.kind !== "limited") return limit

  return { kind: "limited", value: Math.max(limit.value - quota.usedQuota, 0) }
}

// 使用率。無制限・未計測では出さない（null）。上限 0 は 100% 相当とする。
export const quotaUsagePercentage = (quota: Quota): number | null => {
  const limit = quotaLimit(quota)
  if (limit.kind !== "limited") return null
  if (limit.value === 0) return 100

  return Math.min(Math.round((quota.usedQuota / limit.value) * 100), 100)
}

// 上限の表示文字列。「未計測」を 0 と描かず、「上限 0」を無制限と描かないための唯一の出口。
export const quotaLimitLabel = (limit: QuotaLimit): string => {
  switch (limit.kind) {
    case "unlimited":
      return t("no_limited")
    case "unmeasured":
      return t("quota_unmeasured")
    default:
      return limit.value.toLocaleString()
  }
}
